/**
 * usePriorityBackbone — single source of truth for "is this entity a
 * priority backbone item?" lookups across the app.
 *
 * Fetches (topics, markets, companies) once, unions their priority slugs
 * into a normalized Set. Cache lives 5 minutes (these lists barely move).
 * Callers get `has(tag)` and `prioritySet` back — both accept raw display
 * strings ("EDF", "Europe", "AI") and normalize before lookup.
 */
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchers, qk } from "@/lib/queryClient";

// Priority company slugs (curated backbone 15). Kept as a fallback so we
// still highlight them when /api/companies hasn't come back yet.
export const PRIORITY_COMPANY_SLUGS = new Set([
  "planet-fitness", "puregym", "basic-fit", "life-time", "leejam-sports",
  "technogym", "abc-fitness", "matrix-fitness", "oura", "whoop",
  "wellhub", "xponential-fitness", "lifefit-group", "rsg-group", "smartfit",
]);

/**
 * Normalize a display tag → canonical slug:
 *   "EDF"          → "edf"
 *   "Life Time"    → "life-time"
 *   "L'Oréal & Co" → "l-oreal-and-co"
 *   "north_america"→ "north-america"
 */
export function slugify(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function usePriorityBackbone() {
  const [topicsQ, marketsQ, companiesQ] = useQueries({
    queries: [
      {
        queryKey: qk.topics(),
        queryFn: fetchers.topics,
        staleTime: 5 * 60_000,
      },
      {
        queryKey: qk.markets(),
        queryFn: fetchers.markets,
        staleTime: 5 * 60_000,
      },
      {
        queryKey: qk.companies(),
        queryFn: () => fetchers.companies({ limit: 200 }),
        staleTime: 5 * 60_000,
      },
    ],
  });

  const prioritySet = useMemo(() => {
    const set = new Set();
    // Priority topics
    for (const t of topicsQ.data?.data || []) {
      if (t.is_priority && t.slug) {
        set.add(slugify(t.slug));
        if (t.name) set.add(slugify(t.name));
      }
    }
    // Priority markets (regions + priority countries)
    for (const m of marketsQ.data?.data || []) {
      if (m.is_priority && m.slug) {
        set.add(slugify(m.slug));
        if (m.name) set.add(slugify(m.name));
      }
    }
    // Priority companies — curated backbone set
    for (const c of companiesQ.data?.data || []) {
      if (PRIORITY_COMPANY_SLUGS.has(c.slug) || c.priority === "critical" || c.priority === "high" || c.tier === 1) {
        if (c.slug) set.add(slugify(c.slug));
        if (c.name) set.add(slugify(c.name));
      }
    }
    // Fallback set — always at least the curated 15
    for (const s of PRIORITY_COMPANY_SLUGS) set.add(s);
    return set;
  }, [topicsQ.data, marketsQ.data, companiesQ.data]);

  const has = useMemo(
    () => (tag) => prioritySet.has(slugify(tag)),
    [prioritySet],
  );

  return {
    prioritySet,
    has,
    isPending: topicsQ.isPending || marketsQ.isPending || companiesQ.isPending,
  };
}
